import { Component, OnInit, OnDestroy, HostListener, signal, computed, effect } from '@angular/core';
import { load } from '@cashfreepayments/cashfree-js';
import { ToasterService } from '../services/toaster.service';
import { Router } from '@angular/router';
import { PoojaModalService } from './payment-modal.service';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '../services/loader.service';
import { FlexModule } from '@ngbracket/ngx-layout/flex';
import { NgIf, NgFor, NgClass, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'environments/environment';
import { ACTIVE_WALLETS, ALL_BANKS, SUGGESTED_BANKS, CASHFREE_STYLE, SUPPORT_PHONE } from 'app/app.constants';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss'],
  imports: [FlexModule, NgIf, NgFor, NgClass, DecimalPipe, FormsModule]
})
export class PaymentModalComponent implements OnInit, OnDestroy {
  templeName: string;
  templeLogo: string;
  paymentTotal = 0;
  convenienceCharge = 0;
  bookingDetails: any;
  countdown = signal(600);
  userPhone: string = '';
  cashfree: any;
  countdownInterval: any;

  isProcessing = signal(false);
  activeTab = signal<'upi' | 'card' | 'netbanking' | 'wallet'>('upi');

  formattedTime = computed(() => {
    const time = this.countdown();
    const minutes: number = Math.floor(time / 60);
    const seconds: number = time % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  });

  isCardInitialized = false;
  isNetbankingInitialized = false;
  isWalletInitialized = false;
  activeComponent: any = null;
  showCancelConfirm = false;
  isMobile = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkMobile();
  }

  checkMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  // Payment components
  upiQrComponent: any;
  isUpiQrReady = false;
  upiCollectComponent: any;
  cardNumberComponent: any;
  cardHolderComponent: any;
  cardExpiryComponent: any;
  cardCvvComponent: any;
  netbankingComponent: any;
  walletComponent: any;

  // Popular Banks Components
  netbankingComponents: Map<string, any> = new Map();

  // UPI App components
  gPayComponent: any;
  phonePeComponent: any;
  paytmComponent: any;
  amazonPayComponent: any;
  bhimComponent: any;
  otherUpiComponent: any;

  walletComponents: Map<string, any> = new Map();

  // Expose constants to template
  allBanks = ALL_BANKS;
  suggestedBanks = SUGGESTED_BANKS;
  activeWallets = ACTIVE_WALLETS;

  selectedBankCode: string = '';
  selectedWalletCode: string = '';

  private _searchTerm: string = '';
  get searchTerm() { return this._searchTerm; }
  set searchTerm(val: string) {
    this._searchTerm = val;
  }

  get filteredBanks() {
    if (!this.searchTerm) return this.allBanks;
    const term = this.searchTerm.toLowerCase();
    return this.allBanks.filter(bank =>
      bank.name.toLowerCase().includes(term) ||
      bank.code.toLowerCase().includes(term)
    );
  }

  constructor(
    private toastr: ToasterService,
    private router: Router,
    public activeModal: NgbActiveModal,
    private paymentModalService: PoojaModalService,
    private loaderService: LoaderService
  ) {
    effect(() => {
      const val = this.isProcessing();
      const tab = this.activeTab();
      if (val && tab !== 'upi') {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    });
  }

  async ngOnInit() {
    const currTemple = JSON.parse(localStorage.getItem('temple'))[0];
    this.templeName = currTemple.name;
    this.templeLogo = currTemple.imageUrl;
    this.bookingDetails = JSON.parse(localStorage.getItem('success'));
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    this.userPhone = currentUser?.phoneNo || SUPPORT_PHONE; // Default from constants if missing


    const baseAmount = Number(this.bookingDetails.amount || 0);
    const backendCharge = this.bookingDetails.convenienceCharge;

    this.convenienceCharge = (backendCharge != null)
      ? Number(backendCharge)
      : this.getServiceCharge(
        baseAmount,
        Number(currTemple.convenienceCharge || 0)
      );

    // Fix: Using 2 decimal places to match the backend order/session amount
    // Rounding to whole number was causing a mismatch with the actual charged amount.
    this.paymentTotal = Number((baseAmount + this.convenienceCharge).toFixed(2));

    this.checkMobile();
    if (this.bookingDetails.paymentSessionId) {
      await this.initializeCashfreeElements(this.bookingDetails.paymentSessionId);
    }

    this.startCountdown();
  }

  ngOnDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    document.body.style.overflow = '';
  }

  async initializeCashfreeElements(paymentSessionId: string) {
    try {
      // Initialize Cashfree SDK
      const mode = environment.production ? 'production' : 'sandbox';
      // const mode = 'production';
      console.log('Initializing Cashfree SDK in mode:', mode);
      this.cashfree = await load({ mode });

      // Initialize default tab component (UPI)
      await this.createUpiComponents();

      // Other components will be initialized lazily on tab switch
    } catch (error) {
      console.error('Error initializing Cashfree Elements:', error);
      this.toastr.error('Failed to load payment form', 'Error');
    }
  }

  async createUpiComponents() {
    // UPI QR Code
    this.upiQrComponent = this.cashfree.create('upiQr', {
      values: {
        size: '200px'
      }
    });

    this.upiQrComponent.on('loaderror', (data: any) => {
      console.error('UPI QR error:', data.error);
    });

    this.upiQrComponent.on('ready', () => {
      console.log('UPI QR component ready');
      this.isUpiQrReady = true;
      // Automatically trigger pay for QR once ready
      this.payWithUpiQr();
    });

    this.upiQrComponent.mount('#upi-qr-container');

    // UPI Apps for Mobile
    if (this.isMobile) {
      // GPay
      this.gPayComponent = this.cashfree.create('upiApp', {
        values: { upiApp: 'gpay', buttonText: 'Google Pay', buttonIcon: true }
      });
      this.gPayComponent.on('click', () => this.processPayment(this.gPayComponent));
      this.gPayComponent.mount('#gpay-app-container');

      // PhonePe
      this.phonePeComponent = this.cashfree.create('upiApp', {
        values: { upiApp: 'phonepe', buttonText: 'PhonePe', buttonIcon: true }
      });
      this.phonePeComponent.on('click', () => this.processPayment(this.phonePeComponent));
      this.phonePeComponent.mount('#phonepe-app-container');

      // Paytm
      this.paytmComponent = this.cashfree.create('upiApp', {
        values: { upiApp: 'paytm', buttonText: 'Paytm', buttonIcon: true }
      });
      this.paytmComponent.on('click', () => this.processPayment(this.paytmComponent));
      this.paytmComponent.mount('#paytm-app-container');

      // Other Apps (Generic Intent Picker)
      this.otherUpiComponent = this.cashfree.create('upiApp', {
        values: { upiApp: 'default', buttonText: 'Other UPI Apps', buttonIcon: true }
      });
      this.otherUpiComponent.on('click', () => this.processPayment(this.otherUpiComponent));
      this.otherUpiComponent.mount('#other-upi-container');
    }

    // UPI Collect (UPI ID)
    this.upiCollectComponent = this.cashfree.create('upiCollect', {
      values: {
        placeholder: 'Enter UPI ID (e.g., user@upi)'
      }
    });

    this.upiCollectComponent.on('loaderror', (data: any) => {
      console.error('UPI Collect error:', data.error);
    });

    this.upiCollectComponent.on('ready', () => {
      console.log('UPI Collect component ready');
    });

    this.upiCollectComponent.mount('#upi-collect-container');
  }

  async createCardComponents() {
    // Card Number
    this.cardNumberComponent = this.cashfree.create('cardNumber', {
      values: {
        placeholder: 'Card Number'
      }
    });

    this.cardNumberComponent.on('loaderror', (data: any) => {
      console.error('Card number error:', data.error);
    });

    this.cardNumberComponent.mount('#card-number');

    // Card Holder
    this.cardHolderComponent = this.cashfree.create('cardHolder', {
      values: {
        placeholder: 'Cardholder Name'
      }
    });

    this.cardHolderComponent.mount('#card-holder');

    // Card Expiry
    this.cardExpiryComponent = this.cashfree.create('cardExpiry');
    this.cardExpiryComponent.mount('#card-expiry');

    // Card CVV
    this.cardCvvComponent = this.cashfree.create('cardCvv');
    this.cardCvvComponent.mount('#card-cvv');
  }

  async createNetbankingSuggestedComponents() {
    this.loaderService.secondaryLoader.next(true);
    try {
      // Create for Suggested
      await this.createBankComponents(this.suggestedBanks, 'nb-suggested', this.netbankingComponents);
      // Create for all others
      await this.createBankComponents(this.allBanks, 'nb-all', this.allBankComponents);
      this.isNetbankingInitialized = true;
    } finally {
      this.loaderService.secondaryLoader.next(false);
    }
  }

  allBankComponents: Map<string, any> = new Map();

  async createBankComponents(bankList: any[], idPrefix: string, storageMap: Map<string, any>) {
    for (const bank of bankList) {
      if (storageMap.has(bank.code)) continue;

      try {
        const component = this.cashfree.create('netbanking', {
          values: {
            netbankingBankName: bank.code,
            buttonText: bank.name,
            buttonIcon: true
          },
          style: {
            base: {
              fontSize: '15px',
              color: '#12213d',
              fontFamily: 'Inter, sans-serif',
              fontWeight: '600'
            }
          }
        });

        component.on('click', async () => {
          this.selectedBankCode = bank.code;
          await this.processPayment(component);
        });

        storageMap.set(bank.code, component);

        // Mount if element exists
        setTimeout(() => {
          const elementId = `${idPrefix}-${bank.code}`;
          if (document.getElementById(elementId)) {
            component.mount(`#${elementId}`);
          }
        }, 100);

      } catch (e) {
        console.error(`Error creating component for ${bank.name}:`, e);
      }
    }
  }

  isBankMatch(bank: any): boolean {
    if (!this.searchTerm) return true;
    const term = this.searchTerm.toLowerCase();
    return bank.name.toLowerCase().includes(term) || bank.code.toLowerCase().includes(term);
  }

  get hasSuggestedMatches(): boolean {
    if (!this.searchTerm) return true;
    return this.suggestedBanks.some(bank => this.isBankMatch(bank));
  }

  get hasSearchResults(): boolean {
    if (!this.searchTerm) return true;
    return this.allBanks.some(bank => this.isBankMatch(bank));
  }

  async onBankSelect(bankCode: string) {
    // Only return if it's the same bank being re-clicked while already processing
    if (!bankCode || (this.isProcessing() && this.selectedBankCode === bankCode && this.activeTab() === 'netbanking')) {
      return;
    }

    this.selectedBankCode = bankCode;
    this.isProcessing.set(true);
    this.loaderService.secondaryLoader.next(true);

    // Clear previous generic component if it exists
    if (this.netbankingComponent) {
      try {
        this.netbankingComponent.unmount();
      } catch (e) {
        console.warn('Error unmounting component:', e);
      }
    }

    // Create new one for selected bank
    this.netbankingComponent = this.cashfree.create('netbanking', {
      values: { netbankingBankName: bankCode },
      style: CASHFREE_STYLE
    });

    // Safety timeout to prevent getting stuck in "Processing" state
    const safetyTimeout = setTimeout(() => {
      if (this.isProcessing() && this.activeTab() === 'netbanking') {
        console.warn('Netbanking ready event timed out');
        this.isProcessing.set(false);
        this.loaderService.secondaryLoader.next(false);
      }
    }, 5000);

    this.netbankingComponent.on('ready', async () => {
      clearTimeout(safetyTimeout);
      console.log('Netbanking component ready for:', bankCode);
      await this.payWithNetbanking();
    });

    this.netbankingComponent.on('loaderror', (data: any) => {
      clearTimeout(safetyTimeout);
      console.error('Netbanking load error:', data.error);
      this.toastr.error('Failed to initialize bank payment', 'Error');
      this.isProcessing.set(false);
      this.loaderService.secondaryLoader.next(false);
    });

    // Mount to the container
    try {
      this.netbankingComponent.mount('#netbanking-container');
    } catch (e) {
      console.error('Error mounting netbanking component:', e);
      this.isProcessing.set(false);
      this.loaderService.secondaryLoader.next(false);
    }
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
  }

  async createWalletComponents() {
    for (const wallet of ACTIVE_WALLETS) {
      try {
        const component = this.cashfree.create('wallet', {
          values: {
            provider: wallet.code,
            phone: this.userPhone.replace(/\D/g, '').replace(/^91/, ''),
            buttonText: wallet.name,
            buttonIcon: true
          }
        });
        component.on('loaderror', (data: any) => {
          console.error(`Wallet ${wallet.name} load error:`, data.error);
        });
        component.on('click', () => this.processPayment(component));
        this.walletComponents.set(wallet.code, component);
        // Mount into each wallet's dedicated container
        setTimeout(() => {
          component.mount(`#wallet-${wallet.code}`);
        }, 50);
      } catch (e) {
        console.error(`Failed to create wallet component for ${wallet.name}:`, e);
      }
    }
  }

  switchTab(tab: 'upi' | 'card' | 'netbanking' | 'wallet') {
    this.activeTab.set(tab);

    // Slight delay to ensure DOM is updated (removed d-none) before mounting
    setTimeout(async () => {
      if (tab === 'card' && !this.isCardInitialized) {
        await this.createCardComponents();
        this.isCardInitialized = true;
      } else if (tab === 'netbanking' && !this.isNetbankingInitialized) {
        await this.createNetbankingSuggestedComponents();
        this.isNetbankingInitialized = true;
      } else if (tab === 'wallet' && !this.isWalletInitialized) {
        await this.createWalletComponents();
        this.isWalletInitialized = true;
      }
    }, 100);
  }

  async payWithUpiQr() {
    if (!this.isUpiQrReady) {
      console.warn('UPI QR component not yet ready');
      return;
    }
    await this.processPayment(this.upiQrComponent);
  }

  async payWithUpiApp(component: any) {
    // This is now handled by the SDK's .on('click') listener
    this.processPayment(component);
  }

  async payWithUpiCollect() {
    await this.processPayment(this.upiCollectComponent);
  }

  async payWithCard() {
    // For card payment, pass all card components as array
    await this.processPayment(this.cardNumberComponent);
  }

  async payWithNetbanking(bankComponent?: any) {
    const component = bankComponent || this.netbankingComponent;
    if (component) {
      await this.processPayment(component);
    } else {
      console.warn('No netbanking component selected');
    }
  }

  async payWithWallet(walletCode: string) {
    const component = this.walletComponents.get(walletCode);
    if (component) await this.processPayment(component);
  }

  async processPayment(paymentComponent: any) {
    // If it's already processing, we only block if it's the same component being clicked again
    // For QR, it will be automatically "processing" once ready.
    // If the user then interacts with another component (like UPI ID or Cards), we should allow it.

    const isQr = paymentComponent === this.upiQrComponent;

    // If we are already processing and it's NOT the QR (meaning it's a button click on a regular method),
    // and the new component is different, we allow it.
    // But we should prevent multiple simultaneous button clicks for the same method.
    if (this.isProcessing() && !isQr && paymentComponent === this.activeComponent) {
      return;
    }

    this.activeComponent = paymentComponent;
    this.isProcessing.set(true);

    // Only show global loader for non-QR payments (like UPI ID or Cards)
    if (!isQr) {
      this.loaderService.secondaryLoader.next(true);
    }

    try {
      // Get Session ID with fallback to orderToken
      const sessionId = this.bookingDetails.paymentSessionId || this.bookingDetails.orderToken;

      // Open a centered popup window for certain methods (like Netbanking/Wallets)
      let redirectTarget: string = '_modal';
      if (paymentComponent === this.upiQrComponent) {
        redirectTarget = '_self';
      }
      else if (this.activeTab() === 'upi' && paymentComponent === this.upiCollectComponent) {
        // UPI Collect redirects to bank/UPI app — must use _self to avoid popup block
        redirectTarget = '_self';
      }
      if (this.activeTab() === 'netbanking' || this.activeTab() === 'wallet' || this.activeTab() === 'card') {
        const popupName = 'cashfree_payment_popup';
        const popupWidth = 800;
        const popupHeight = 700;
        const left = window.screenX + (window.outerWidth - popupWidth) / 2;
        const top = window.screenY + (window.outerHeight - popupHeight) / 2;

        const popup = window.open(
          'about:blank',
          popupName,
          `width=${popupWidth},height=${popupHeight},top=${top},left=${left},scrollbars=yes,resizable=yes`
        );

        if (!popup) {
          const method = this.activeTab() === 'netbanking' ? 'Netbanking' : 'Wallet';
          this.toastr.error(`Popup blocked! Please allow popups for this site to complete ${method} payment.`, 'Error');
          this.isProcessing.set(false);
          this.loaderService.secondaryLoader.next(false);
          return;
        }

        redirectTarget = popupName;

        // Watch for popup close
        const popupWatcher = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupWatcher);
            if (this.isProcessing()) {
              this.toastr.warning('Payment window was closed. If the amount was deducted, please wait for status update.', 'Payment Interrupted');
              this.activeComponent = null;
              this.isProcessing.set(false);
              this.loaderService.secondaryLoader.next(false);
            }
          }
        }, 800);
      }

      const paymentOptions = {
        paymentMethod: paymentComponent,
        paymentSessionId: sessionId,
        returnUrl: `${window.location.origin}/pooja-booking/success?order_id={order_id}`,
        redirectTarget: redirectTarget,
      };

      const result = await this.cashfree.pay(paymentOptions);

      if (result.error) {
        console.error('Payment error:', result.error);
        this.toastr.error(result.error.message, 'Payment Failed');
        this.activeComponent = null;
        this.isProcessing.set(false);
        if (!isQr) {
          this.loaderService.secondaryLoader.next(false);
        }
      } else if (result.redirect) {
        console.log('Redirecting for payment...');
      } else if (result.paymentDetails) {
        this.handlePaymentSuccess(result.paymentDetails);
      } else {
        // If it's a popup flow, we stay in processing state until the popup is closed (handled by watcher)
        if (isQr || redirectTarget === 'cashfree_payment_popup') {
          // Stay in processing state
        } else {
          this.activeComponent = null;
          this.isProcessing.set(false);
          this.loaderService.secondaryLoader.next(false);
        }
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      this.toastr.error(error?.message || 'Payment processing failed', 'Error');
      this.activeComponent = null;
      this.isProcessing.set(false);
      if (!isQr) {
        this.loaderService.secondaryLoader.next(false);
      }
    }
  }

  handlePaymentSuccess(paymentDetails: any) {
    console.log('Payment successful:', paymentDetails);
    localStorage.removeItem('orderToken');
    localStorage.setItem('paymentresponse', JSON.stringify(paymentDetails));
    this.activeModal.close();
    this.router.navigate(['/pooja-booking/success']);
  }

  getServiceCharge(total: number, convenience: number): number {
    const charge = this.upperRoundOff((total * (convenience || 0)) / 100);
    if (charge === 0) {
      return 0;
    }
    return charge < 1 ? 1 : charge;
  }

  getFormattedTime(): string {
    return this.formattedTime();
  }

  upperRoundOff(num: number): number {
    return Math.ceil(num * 100) / 100;
  }

  startCountdown() {
    this.countdownInterval = setInterval(() => {
      this.countdown.update(c => c - 1);
      if (this.countdown() <= 0) {
        clearInterval(this.countdownInterval);
        this.toastr.error('Payment session expired', 'Timeout');
        this.closeModal();
      }
    }, 1000);
  }

  attemptClose() {
    this.showCancelConfirm = true;
  }

  cancelClose() {
    this.showCancelConfirm = false;
  }

  confirmClose() {
    this.showCancelConfirm = false;
    this.closeModal();
  }

  closeModal() {
    console.log('Closing modal');
    this.loaderService.secondaryLoader.next(true);

    this.paymentModalService
      .fetchOrderStatus(this.bookingDetails.orderId)
      .pipe(
        finalize(() => {
          this.loaderService.secondaryLoader.next(false);
        })
      )
      .subscribe(
        (res) => {
          console.log('Order status:', res);
          if (res && res.order_status === 'PAID') {
            localStorage.removeItem('orderToken');
            localStorage.setItem('paymentresponse', JSON.stringify(res));
            this.activeModal.close();
            this.router.navigate(['/pooja-booking/success']);
          } else {
            this.activeModal.close();
            this.router.navigate(['/pooja-booking/transaction-failure']);
          }
        },
        (error) => {
          console.error('Error fetching order status:', error);
          this.activeModal.close();
        }
      );
  }
}
